import {
  Button,
  Center,
  Container,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { ProdClient } from 'client/client';
import { PaginationNew } from 'components/base/PaginationNew';
import { Contact as ContactModel } from 'models';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { usePaginatedPeople } from 'store/paginated-people';
import { withTranslations } from 'store/translations';
import { alphabeticalSort } from 'utils/alphabeticalSort';
import { isDefined } from 'utils/isDefined';

import Person from './Person';

interface FetchProps {
  page: number;
  city?: string;
  department?: string;
  names?: string[];
}

const People = () => {
  const {
    cities,
    selectedCity,
    setSelectedCity,
    departments,
    selectedDepartment,
    setSelectedDepartment,
    pagination,
    people,
    setPeople,
    setPagination,
    names,
  } = usePaginatedPeople();
  const { common, aria } = withTranslations();
  const [filteredDepartments, setFilteredDepartments] = useState<string[]>();
  const [filteredCities, setFilteredCities] = useState<string[]>();
  const [shouldShowHelperText, setShouldShowHelperText] = useState(false);
  const [filterByNameValue, setFilterByNameValue] = useState('');
  const [filteredPeople, setFilteredPeople] = useState<string[]>();
  const [fetchTimeout, setFetchTimeout] = useState<NodeJS.Timeout>();
  const router = useRouter();
  const { setDepartments } = usePaginatedPeople();

  const sortedDepartments = departments
    ? departments.sort((a, b) => {
        const aStr = a
          .replace(/\b\d{4,}\b/g, '')
          .replace(/^\s+/, '')
          .replace(/^[ -]+|[ -]+$/g, '');
        const bStr = b
          .replace(/\b\d{4,}\b/g, '')
          .replace(/^\s+/, '')
          .replace(/^[ -]+|[ -]+$/g, '');

        return aStr.localeCompare(bStr);
      })
    : [];
  const languageCodename = router.locale ?? process.env.NEXT_PUBLIC_DEFAULT_LOCALE!;
  const limit = 1000;
  let skip = 0;
  let filterData = [] as ContactModel[];
  useEffect(() => {
    const getDropdown = async (languageCodename: string) => {
      const response = await ProdClient.items<ContactModel>()
        .type('contact')
        .languageParameter(languageCodename)
        .includeTotalCountParameter()
        .elementsParameter(['department', 'city'])
        .limitParameter(limit)
        .skipParameter(skip)
        .toPromise();
      if (!response.data.pagination.nextPage) {
        skip = 0;
        return;
      }
      filterData = filterData.concat(...response.data.items);
      const filterOptions = filterData
        .reduce<ContactModel[]>((acc, item) => {
          if (item.elements.city.value) {
            item.elements.city.value = item.elements.city.value.split(',')[0].trim();
          }
          acc.push(item);
          return acc;
        }, [])
        .map((item) => item.elements);
      const dept = new Array<string>();
      filterOptions.forEach(
        (item) => !dept.includes(item.department.value.trim()) && dept.push(item.department.value.trim()),
      );
      setDepartments(dept);
      skip += limit;
      await getDropdown(languageCodename);
    };

    getDropdown(languageCodename);
 
    fetchPeople({ page: 1, city: selectedCity, department: selectedDepartment });
  }, [router.locale]);

  const numSelectedFilters = useMemo(() => {
    let count = 0;

    Boolean(selectedCity) && count++;
    Boolean(selectedDepartment) && count++;
    Boolean(filterByNameValue) && count++;

    return count;
  }, [selectedCity, selectedDepartment, filterByNameValue]);

  const fetchPeople = async ({ page, city, department, names }: FetchProps) => {
    try {
      const peopleData = await fetch(
        `/api/get-people?pageNum=${page}&locale=${router.locale}${
          names
            ? `&names=${encodeURIComponent(
                names
                  ?.filter((item, pos) => {
                    return names.indexOf(item) == pos;
                  })
                  .toString(),
              )}`
            : ''
        }${city ? `&city=${encodeURIComponent(city)}` : ''}${
          department ? `&department=${encodeURIComponent(department)}` : ''
        }`,
        {
          method: 'POST',
        },
      ).then((res) => res.json());
      const people = peopleData.items.items
        .filter(isDefined)
        .map((item) => item.elements)
        .sort((a, b) => alphabeticalSort(a.displayName.value, b.displayName.value, 0));

      // Creating a temporary array to filter and store departments so there's no duplicates
      let uniqueDepartments: string[] = [];
      peopleData.departments &&
        peopleData.departments.items.forEach((item) => {
          // If there's no duplicate then push it to the temporary array
          if (!uniqueDepartments.includes(item.elements.department.value)) {
            uniqueDepartments.push(item.elements.department.value);
          }
        });

      let uniqueCities: string[] = [];
      peopleData.cities &&
        peopleData.cities.items.forEach((item) => {
          if (!uniqueCities.includes(item.elements.city.value)) {
            uniqueCities.push(item.elements.city.value);
          }
        });

      uniqueCities = uniqueCities.sort();

      // Sorting the departments based on formatting for title
      uniqueDepartments = uniqueDepartments.sort((a, b) => {
        const aStr = a
          .replace(/\b\d{4,}\b/g, '')
          .replace(/^\s+/, '')
          .replace(/^[ -]+|[ -]+$/g, '');
        const bStr = b
          .replace(/\b\d{4,}\b/g, '')
          .replace(/^\s+/, '')
          .replace(/^[ -]+|[ -]+$/g, '');

        return aStr.localeCompare(bStr);
      });
      // Show the updated list of departments
      // If there's no query for city these will not be fetched and the list of departments will fall back to the full list
      setFilteredDepartments(uniqueDepartments);
      setFilteredCities(uniqueCities);
      setPeople(people);
      setPagination(peopleData.items.pagination);
    } catch (err: any) {
      throw new Error(err.message);
    }
  };

  return (
    <Container pb={{ base: 's', lg: 'm' }} pt='s'>
      <Flex
        align='center'
        direction={{ base: 'column', lg: 'row' }}
        gap={{ base: 'xs', lg: '0' }}
        mb={{ base: 's', lg: 'm' }}
      >
        <Flex
          align='center'
          direction={{ base: 'column', lg: 'row' }}
          gap={{ base: 'xs', lg: '0' }}
          w={{ base: '100%', lg: 'auto' }}
        >
          <FormControl mr={{ base: '0', lg: 's' }}>
            <FormLabel mb='3xs'>
              <Text color='mountain.base' textStyle='mobile.body.xs'>
                {common('searchByName')}
              </Text>
            </FormLabel>
            <Input
              bg='gray.98'
              borderColor='mountain.20'
              placeholder={aria('search')}
              value={filterByNameValue}
              onChange={(e) => {
                setFilterByNameValue(e.target.value);
                clearTimeout(fetchTimeout);
                if (e.target.value.length === 0) {
                  setShouldShowHelperText(false);
                  fetchPeople({ page: 1, city: selectedCity, department: selectedDepartment });
                } else if (e.target.value.length > 2) {
                  setShouldShowHelperText(false);
                  const filteredNames = names?.filter((name) =>
                    name.toLowerCase().includes(e.target.value.toLowerCase()),
                  );
                  setFilteredPeople(filteredNames);
                  setFetchTimeout(
                    setTimeout(() => {
                      fetchPeople({
                        page: 1,
                        city: selectedCity,
                        department: selectedDepartment,
                        // Need to send some sort of text that will not be in the name field, so that returning result is null
                        names: filteredNames?.length ? filteredNames : ['{NULL RETURN}'],
                      });
                    }, 1000),
                  );
                } else setShouldShowHelperText(true);
              }}
            />
            <FormHelperText
              display={shouldShowHelperText ? '' : 'none'}
              position={{ base: 'relative', lg: 'absolute' }}
            >
              {common('searchByNameInfo')}
            </FormHelperText>
          </FormControl>
          {cities && (
            <FormControl mr={{ base: '0', lg: 's' }}>
              <FormLabel mb='3xs'>
                <Text color='mountain.base' textStyle='mobile.body.xs'>
                  {common('location')}
                </Text>
              </FormLabel>
              <Select
                bg='gray.98'
                borderColor='mountain.20'
                borderRadius='0'
                w={{ base: '100%', lg: '200px' }}
                value={selectedCity}
                onChange={(e) => {
                  setSelectedCity(e.target.value);
                  fetchPeople({
                    page: 1,
                    city: e.target.value ? e.target.value : undefined,
                    department: selectedDepartment,
                    names: filteredPeople,
                  });
                }}
              >
                <option value=''>{common('all')}</option>
                {filteredCities?.length
                  ? filteredCities.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))
                  : cities.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
              </Select>
            </FormControl>
          )}
          {departments && (
            <FormControl mr={{ base: '0', lg: 's' }}>
              <FormLabel mb='3xs'>
                <Text color='mountain.base' textStyle='mobile.body.xs'>
                  {common('department')}
                </Text>
              </FormLabel>
              <Select
                bg='gray.98'
                borderColor='mountain.20'
                borderRadius='0'
                w={{ base: '100%', lg: '200px' }}
                value={selectedDepartment}
                onChange={(e) => {
                  setSelectedDepartment(e.target.value);
                  fetchPeople({
                    page: 1,
                    city: selectedCity,
                    department: e.target.value ? e.target.value : undefined,
                    names: filteredPeople,
                  });
                }}
              >
                <option value=''>{common('all')}</option>
                {/* If we've filtered by city we use the list of Departments in that city
                    Otherwise we show all possible Departments */}
                {filteredDepartments?.length
                  ? filteredDepartments?.map((option) => {
                      const departmentTitle = option
                        .replace(/\b\d{4,}\b/g, '')
                        .replace(/^\s+/, '')
                        .replace(/^[ -]+|[ -]+$/g, '');
                      return (
                        <option key={option} value={option}>
                          {departmentTitle}
                        </option>
                      );
                    })
                  : sortedDepartments.map((option) => {
                      const departmentTitle = option
                        .replace(/\b\d{4,}\b/g, '')
                        .replace(/^\s+/, '')
                        .replace(/^[ -]+|[ -]+$/g, '');
                      return (
                        <option key={option} value={option}>
                          {departmentTitle}
                        </option>
                      );
                    })}
              </Select>
            </FormControl>
          )}
        </Flex>
      </Flex>
      <Flex
        align='center'
        direction={{ base: 'column', lg: 'row' }}
        gap={{ base: 'xs', lg: '0' }}
        mb={{ base: 's', lg: 'xl' }}
      >
        {pagination && (
          <Text
            visibility={pagination.count ? 'visible' : 'hidden'}
            alignSelf={{ base: 'start', lg: 'none' }}
            color='mountain.base'
            flex='1'
            order={{ base: '2', lg: '1' }}
            textStyle='mobile.body.medium.s'
            dangerouslySetInnerHTML={{
              __html: common('showingResults', {
                itemsShown: `${pagination.skip + 1} - ${pagination.skip + pagination.count}`,
                total: pagination.totalCount,
              }),
            }}
          />
        )}
        <Button
          alignSelf={{ base: 'end', lg: 'none' }}
          color='cyan.web'
          disabled={numSelectedFilters === 0}
          order={{ base: '1', lg: '2' }}
          textStyle='mobile.body.medium.s'
          variant='ghost'
          onClick={() => {
            setSelectedCity('');
            setSelectedDepartment('');
            setFilteredPeople([]);
            setFilterByNameValue('');
            fetchPeople({ page: 1, city: '', department: '' });
          }}
        >
          {common('clearFilters')} ({numSelectedFilters})
        </Button>
      </Flex>
      {people?.length ? (
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          spacingX={{ base: 0, md: 'xs' }}
          spacingY={{ base: 's', lg: 'xl' }}
          pb={{ lg: 'lr' }}
        >
          {people.map((person) => {
            if (person.displayName.value !== '') return <Person hasContact {...person} key={person.id.value} />;
          })}
        </SimpleGrid>
      ) : (
        <Center>
          <Text color='mountain.base' textStyle={{ base: 'mobile.body.medium.s', lg: 'desktop.body.medium.l' }}>
            {common('noResultsFound')}
          </Text>
        </Center>
      )}
      {pagination && (
        <Flex mt={{ base: 's', lg: 'xl' }} justifyContent='center'>
          <PaginationNew
            total={pagination?.totalCount || 0}
            perPage={12}
            current={pagination.skip / pagination.limit + 1}
            onNavigate={(page) =>
              fetchPeople({ page, city: selectedCity, department: selectedDepartment, names: filteredPeople })
            }
          />
        </Flex>
      )}
    </Container>
  );
};

export default People;
